import { Fragment, useState } from 'react';
import { Plural, T, Var, useGT } from 'gt-react';
import { cn } from '@/lib/utils';
import {
  buildGithubFileDiff,
  githubDiffTotals,
  splitHunkRows,
  type DiffFileInput,
  type GithubDiffLine,
  type GithubFileDiff,
} from './buildGithubDiff';

export type GithubDiffLayout = 'unified' | 'split';

const LINE_TONE: Record<GithubDiffLine['kind'], string> = {
  context: '',
  add: 'bg-green-50 dark:bg-green-950/40',
  del: 'bg-red-50 dark:bg-red-950/40',
};

const SPAN_TONE: Record<GithubDiffLine['kind'], string> = {
  context: '',
  add: 'bg-green-200/80 dark:bg-green-800/60',
  del: 'bg-red-200/80 dark:bg-red-800/60',
};

function lineSign(kind: GithubDiffLine['kind']): string {
  if (kind === 'add') return '+';
  if (kind === 'del') return '−';
  return ' ';
}

function LineText({ line }: { line: GithubDiffLine }) {
  if (line.text.length === 0) return <span>{'\u00a0'}</span>;
  return line.spans.map((span, index) => (
    <span
      key={`${String(index)}:${span.text}`}
      className={span.changed ? SPAN_TONE[line.kind] : undefined}
    >
      {span.text}
    </span>
  ));
}

function LineNumbers({ line }: { line: GithubDiffLine | null }) {
  return (
    <>
      <span className="inline-block w-10 select-none pr-2 text-right text-muted-foreground">
        {line?.oldNumber ?? ''}
      </span>
      <span className="inline-block w-10 select-none pr-2 text-right text-muted-foreground">
        {line?.newNumber ?? ''}
      </span>
    </>
  );
}

function UnifiedLine({ line }: { line: GithubDiffLine }) {
  return (
    <div
      className={cn(
        'whitespace-pre-wrap break-words px-2 font-mono text-xs leading-5',
        LINE_TONE[line.kind],
      )}
    >
      <LineNumbers line={line} />
      <span className="inline-block w-4 select-none" aria-hidden="true">
        {lineSign(line.kind)}
      </span>
      <LineText line={line} />
    </div>
  );
}

function SplitCell({
  line,
  side,
}: {
  line: GithubDiffLine | null;
  side: 'add' | 'del';
}) {
  const tone = line ? LINE_TONE[line.kind] : 'bg-muted/40';
  return (
    <div
      className={cn(
        'min-w-0 whitespace-pre-wrap break-words px-2 font-mono text-xs leading-5',
        tone,
      )}
    >
      <LineNumbers line={line} />
      <span className="inline-block w-4 select-none" aria-hidden="true">
        {line ? lineSign(line.kind) : ' '}
      </span>
      {line ? <LineText line={line} /> : <span>{'\u00a0'}</span>}
      <span className="sr-only">
        {side === 'del' ? (
          <T context="github-style diff original column">Before</T>
        ) : (
          <T context="github-style diff updated column">After</T>
        )}
      </span>
    </div>
  );
}

function HunkView({
  hunk,
  layout,
}: {
  hunk: GithubFileDiff['hunks'][number];
  layout: GithubDiffLayout;
}) {
  return (
    <div>
      <div className="bg-sky-50 px-3 py-1 font-mono text-xs text-sky-900 dark:bg-sky-950/40 dark:text-sky-100">
        {hunk.header}
      </div>
      {layout === 'unified' ? (
        hunk.lines.map((line) => (
          <UnifiedLine
            key={`${hunk.header}:${line.kind}:${String(line.oldNumber)}:${String(line.newNumber)}`}
            line={line}
          />
        ))
      ) : (
        <div className="grid grid-cols-2 divide-x">
          {splitHunkRows(hunk.lines).map((row, index) => (
            <Fragment key={`${hunk.header}:${String(index)}`}>
              <SplitCell line={row.left} side="del" />
              <SplitCell line={row.right} side="add" />
            </Fragment>
          ))}
        </div>
      )}
    </div>
  );
}

function FileDiff({
  file,
  layout,
  showUnchanged,
}: {
  file: GithubFileDiff;
  layout: GithubDiffLayout;
  showUnchanged: boolean;
}) {
  const gt = useGT();
  const changed = file.additions + file.deletions > 0;
  const [expanded, setExpanded] = useState(changed || showUnchanged);
  return (
    <details
      open={expanded}
      onToggle={(event) => setExpanded(event.currentTarget.open)}
      className="overflow-hidden rounded-md border"
    >
      <summary className="flex cursor-pointer items-center gap-3 bg-muted/40 px-3 py-2 text-sm">
        <span className="font-mono text-xs">{file.path}</span>
        <span className="text-xs text-green-700 dark:text-green-400">
          +<Var>{file.additions}</Var>
        </span>
        <span className="text-xs text-red-700 dark:text-red-400">
          −<Var>{file.deletions}</Var>
        </span>
      </summary>
      <div
        className="overflow-x-auto border-t"
        aria-label={gt('Diff of {path}', {
          context: 'github-style diff file region',
          path: file.path,
        })}
      >
        {file.hunks.map((hunk) => (
          <HunkView key={hunk.header} hunk={hunk} layout={layout} />
        ))}
      </div>
    </details>
  );
}

function DiffSummary({ files }: { files: GithubFileDiff[] }) {
  const totals = githubDiffTotals(files);
  if (totals.files === 0) {
    return (
      <p role="status" className="text-sm text-muted-foreground">
        <T context="github-style diff with no edits">No Changes</T>
      </p>
    );
  }
  return (
    <p className="text-sm">
      <Var>{totals.files}</Var>{' '}
      <Plural n={totals.files} one="file changed" other="files changed" />{' '}
      <span className="text-green-700 dark:text-green-400">
        +<Var>{totals.additions}</Var>
      </span>{' '}
      <span className="text-red-700 dark:text-red-400">
        −<Var>{totals.deletions}</Var>
      </span>
    </p>
  );
}

/**
 * A GitHub files-changed view: one collapsible file, hunk headers, line numbers,
 * and red/green rows. Unchanged files stay hidden until asked for.
 */
export function GithubDiffView({
  files,
  layout,
  showUnchanged,
}: {
  files: DiffFileInput[];
  layout: GithubDiffLayout;
  showUnchanged: boolean;
}) {
  const diffs = files.map(buildGithubFileDiff);
  const visible = showUnchanged
    ? diffs
    : diffs.filter((file) => file.additions + file.deletions > 0);
  return (
    <div className="flex flex-col gap-3 p-3">
      <DiffSummary files={diffs} />
      {visible.map((file) => (
        <FileDiff
          key={`${file.path}:${String(showUnchanged)}`}
          file={file}
          layout={layout}
          showUnchanged={showUnchanged}
        />
      ))}
    </div>
  );
}
